import React, { useState, useEffect } from "react";
import { database, ref, push, onValue, update, remove } from "./firebase";
import "./BillingSystem.css";
import BossNavigation from "./BossNavigation";

export default function BottlersEntry() {
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [balance, setBalance] = useState("");
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    // Fetch products
    const productsRef = ref(database, "products");
    onValue(productsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.keys(data).map((id) => ({
          id,
          ...data[id],
          quantity: 0,
          price: data[id].rate ?? 0,
        }));
        setProducts(list);
      }
    });

    // Fetch bottlers entries
    const entriesRef = ref(database, "bottlersHistory");
    onValue(entriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.keys(data).map((id) => ({ id, ...data[id] }));
        list.sort((a, b) => new Date(b.date) - new Date(a.date));
        setEntries(list);
      } else {
        setEntries([]);
      }
    });
  }, []);

  const handleQuantityChange = (id, value) => {
    const updated = products.map((p) =>
      p.id === id ? { ...p, quantity: Number(value) } : p
    );
    setProducts(updated);
  };

  const handlePriceChange = (id, value) => {
    const updated = products.map((p) =>
      p.id === id ? { ...p, price: value === "" ? "" : Number(value) } : p
    );
    setProducts(updated);
  };

  const calculateAmount = (price, qty) => Number(price) * Number(qty);

  const totalAmount = products.reduce(
    (sum, p) => sum + calculateAmount(p.price || 0, p.quantity || 0),
    0
  );

  const handleSave = () => {
    if (!name) {
      alert("Enter name first");
      return;
    }

    // Auto mark paid if no balance given
    const enteredBalance = balance === "" ? 0 : Number(balance);
    const isPaid = enteredBalance <= 0;

    const record = {
      name,
      date,
      products: products.map((p) => ({
        name: p.name,
        rate: p.rate,
        price: p.price === "" ? 0 : Number(p.price),
        quantity: Number(p.quantity) || 0,
        amount: calculateAmount(p.price || 0, p.quantity || 0),
      })),
      total: totalAmount,
      balance: enteredBalance,
      isPaid,
    };

    if (editId) {
      update(ref(database, `bottlersHistory/${editId}`), record);
      alert("✅ Entry updated successfully!");
      setEditId(null);
    } else {
      push(ref(database, "bottlersHistory"), record);
      alert("✅ Entry saved successfully!");
    }

    // Reset form
    setProducts(products.map((p) => ({ ...p, quantity: 0, price: p.rate ?? 0 })));
    setName("");
    setBalance("");
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setName(entry.name);
    setDate(entry.date);
    setBalance(entry.balance ?? "");
    const updatedProducts = products.map((p) => {
      const match = entry.products.find((x) => x.name === p.name);
      return {
        ...p,
        quantity: match ? match.quantity : 0,
        price: match ? match.price ?? p.rate ?? 0 : p.rate ?? 0,
      };
    });
    setProducts(updatedProducts);
  };

  const handleDelete = (id) => {
    if (window.confirm("🗑️ Are you sure you want to delete this entry?")) {
      remove(ref(database, `bottlersHistory/${id}`));
      alert("Entry deleted successfully!");
    }
  };

  const handleMarkPaid = (id, entry) => {
    if (window.confirm(`✅ Mark ${entry.name}'s balance as paid?`)) {
      update(ref(database, `bottlersHistory/${id}`), {
        balance: 0,
        isPaid: true,
      });
      alert("✅ Balance marked as paid!");
    }
  };

  // Group entries by date
  const groupedEntries = entries.reduce((acc, e) => {
    if (!acc[e.date]) acc[e.date] = [];
    acc[e.date].push(e);
    return acc;
  }, {});

  return (
    <div className="history-container">
      <h2 className="page-title">💧 Bottlers Entry</h2>
      <BossNavigation />

      {/* Input Form */}
      <div className="form-section">
        <table className="styled-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Price (₹)</th>
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  <input
                    type="number"
                    value={p.quantity || ""}
                    onChange={(e) => handleQuantityChange(p.id, e.target.value)}
                    className="input-small"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    value={p.price === 0 ? 0 : p.price || ""}
                    onChange={(e) => handlePriceChange(p.id, e.target.value)}
                    className="input-small"
                  />
                </td>
                <td>₹{calculateAmount(p.price || 0, p.quantity || 0)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td></td>
              <td></td>
              <td style={{ fontWeight: "bold" }}>₹{totalAmount}</td>
            </tr>
          </tbody>
        </table>

        <div className="entry-controls">
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input-text"
          />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input-date"
          />
          <input
            type="number"
            placeholder="Balance Amount (₹)"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            className="input-small"
          />
          <button className="save-btn" onClick={handleSave}>
            {editId ? "✏️ Update Entry" : "💾 Save Entry"}
          </button>
        </div>
      </div>

      {/* History Table */}
      <h3 className="section-title">📜 Bottlers History</h3>
      {Object.keys(groupedEntries).length === 0 ? (
        <p>No entries yet.</p>
      ) : (
        Object.keys(groupedEntries).map((dateKey) => (
          <div key={dateKey} className="history-table-section">
            <h4 className="date-header">{dateKey}</h4>
            <table className="styled-table">
              <thead>
                <tr>
                  <th>Name</th>
                  {products.map((p, i) => (
                    <th key={i}>{p.name}</th>
                  ))}
                  <th>Total (₹)</th>
                  <th>Balance (₹)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedEntries[dateKey].map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ fontWeight: "bold" }}>{entry.name}</td>
                    {products.map((p, i) => {
                      const product = entry.products.find((x) => x.name === p.name);
                      return (
                        <td key={i}>
                          {product && product.quantity > 0
                            ? `${product.quantity} @ ₹${product.price ?? 0}`
                            : "—"}
                        </td>
                      );
                    })}
                    <td style={{ fontWeight: "bold" }}>₹{entry.total}</td>
                    <td
                      style={{
                        fontWeight: "bold",
                        color: entry.isPaid ? "green" : "red",
                      }}
                    >
                      ₹{entry.balance ?? 0}
                    </td>
                    <td>
                      {entry.isPaid ? (
                        <span style={{ color: "green" }}>✅ Paid</span>
                      ) : (
                        <span style={{ color: "red" }}>⏳ Pending</span>
                      )}
                    </td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEdit(entry)}>
                        ✏️ Edit
                      </button>
                      <button className="delete-btn" onClick={() => handleDelete(entry.id)}>
                        🗑️ Delete
                      </button>
                      {!entry.isPaid && (
                        <button
                          className="paid-btn"
                          onClick={() => handleMarkPaid(entry.id, entry)}
                        >
                          💰 Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}  